/* eslint-disable max-len */
import { ChantContext } from '../gregorio-chant/ChantContext';
import { ChantSyllableElement } from './containers/ChantSyllableElement';
import { DropCap } from './texts/DropCap';
import { Lyric } from './texts/Lyric';
import { Annotation } from './texts/Annotation';
import { GregorioScore } from '../gregorio-chant/structures/GregorioScore';
import { ChantLine } from './containers/ChantLine';
import { GlyphVisualizer } from './visualizers';
import { SHAPE, gregoriotex_determine_glyph_name, determine_leading_shape } from './glyphs/determine_glyph';
import { GregorioType } from '../gregorio-chant/enums/GregorioType';
import { Clef } from './clefs';
import { determine_note_glyph_name } from './glyphs/determine_note_glyph';
import { NabcSnippetVisualizer } from './containers/NabcSnippetVisualizer';
import { Glyph } from './glyphs/Glyph';
import { GregorioSign } from '../gregorio-chant/enums/GregorioSign';
import { Mora } from './visualizers/Mora';
import { Ictus } from './visualizers/Ictus';
import { HorizontalEpisema } from './visualizers/HorizontalEpisema';
import { GregorioShape } from '../gregorio-chant/enums/GregorioShape';
import { GregorioLiquescentia } from '../gregorio-chant/enums/GregorioLiquescentia';
import { Space } from './elements/Space';
import { determine_bar } from './bars/determine_bar';
import { ChantSegmentElement } from './containers/ChantSegmentElement';
import { BarBrace } from './visualizers/BarBrace';
import { GregorioGlyphType } from '../gregorio-chant/enums/GregorioGlyphType';
import { GregorioGlyph } from '../gregorio-chant/structures/GregorioGlyph';
import { GregorioElement } from '../gregorio-chant/structures/GregorioSyllable';
import { compute_fused_shift } from '../gregorio-chant/score_parsing/compute_fused_shift';
import { Custos } from './signs';
import { GregorioNoteElement } from '../gregorio-chant/structures/GregorioNoteElement';
import { Commentary } from './texts/Commentary';
import { is_fused } from '../gregorio-chant/score_parsing/other';
import {
  get_punctum_inclinatum_space_case,
  get_punctum_inclinatum_to_nobar_space_case,
  is_before_linebreak,
  is_puncta_inclinata
} from './utils';
import { GregorioSpace } from '../gregorio-chant/enums/GregorioSpace';
import { DistanceConverter } from './distances/DistanceConverter';
import { max, min } from 'lodash';
import { PunctaInclinata } from './enums/constants';
import { get_default_clef } from '../gregorio-chant/utils/default_clef';
import { TextSpanType } from '../gregorio-chant/structures/TextSpan';
import { AboveLineText } from './texts/AboveLineText';

export abstract class GregorianChantRenderer {
  protected _context!: ChantContext;
  protected _annotation?: Annotation;
  protected _commentary?: Commentary;
  private _specialNotes = [
    GregorioGlyphType.BIVIRGA,
    GregorioGlyphType.DISTROPHA,
    GregorioGlyphType.TRIVIRGA,
    GregorioGlyphType.TRISTROPHA,
    GregorioGlyphType.DISTROPHA_AUCTA,
    GregorioGlyphType.TRISTROPHA_AUCTA,
  ];


  public lines: ChantLine[] = [];
  public notations: ChantSyllableElement[] = [];

  get width(): number {
    return max(this.lines.map((l) => l.width)) ?? 0;
  }

  get top(): number {
    return min(this.lines.map((l) => l.top)) || 0;
  }

  getNextGlyph(elements: GregorioElement[], elementIndex: number, glyphIndex: number): GregorioGlyph | undefined {
    for (let i = elementIndex; i < elements.length; i++) {
      if (i > elementIndex) {
        // eslint-disable-next-line no-param-reassign
        glyphIndex = 0;
      }
      for (let j = glyphIndex; j < elements[i].glyphs.length; j++) {
        const glyph = elements[i].glyphs[j];
        if (glyph.type === GregorioType.GLYPH) {
          return elements[i].glyphs[j];
        }
      }
    }

    return undefined;
  }

  protected prepareNotations(score: GregorioScore) {
    this.notations = [];

    const validation = score.validate();
    if (!validation.success) {
      // throw new Error(validation.errors.join('\n'));
      return;
    }

    const mode = +score.metadata.getField<number>('mode');
    const annotation = score.metadata.getField<string[]>('annotation');
    this._annotation = new Annotation(this._context, mode, annotation);

    const commentary = score.metadata.getField<string>('commentary');
    this._commentary = new Commentary(this._context, commentary);

    score.determineElements();

    let sylIndex = 0;

    if (score.syllables.length > 0 && score.syllables[0].isClef === false) {
      score.syllables.unshift(get_default_clef());
    }

    for (const syllable of score.syllables) {
      let syllableElement =
        new ChantSyllableElement(this._context, syllable, sylIndex);

      if (syllable.textSpans.length > 0) {
        syllableElement.addLyric(new Lyric(
          this._context,
          syllable.textSpans,
          syllable.position,
          syllable.keepWithNext,
        ));
      }

      const aboveLineText = syllable.textSpans.filter((s) => s.type & TextSpanType.AboveLinesText);
      if (aboveLineText.length > 0) {
        syllableElement.addAboveLineText(new AboveLineText(this._context, aboveLineText));
      }

      let segmentElements: ChantSegmentElement[] = [];

      for (const segment of syllable.segments) {
        let segmentElement: ChantSegmentElement | undefined;

        if (!segmentElement) {
          segmentElement = new ChantSegmentElement(this._context, segment);
        }

        for (let elIndex = 0; elIndex < segment.elements.length; elIndex++) {
          const element = segment.elements[elIndex];
          const nextElement = segment.elements[elIndex + 1];
          const prevElement = segment.elements[elIndex - 1];

          if (!segmentElement) {
            segmentElement = new ChantSegmentElement(this._context, segment);
          }

          switch (element.type) {
            case GregorioType.END_OF_LINE:
              segmentElement.endOfLineInfo = element.misc.info.eol;
              break;

            case GregorioType.CLEF:
              // segmentElement.addVisualizer(new Clef(this._context, element));
              // segmentElement.addVisualizer(Space.createFromWidth(this._context, this._context.spaceafterlineclef));
              if (is_before_linebreak(score.syllables, sylIndex, elIndex)) {
                // let next_note_alteration;

                // let next_note_pitch = gregorio_adjust_pitch_into_staff(
                //             score, gregorio_determine_next_pitch(
                //             syllable, element, NULL, &next_note_alteration)
                //             - element->u.misc.clef.pitch_difference);

                //     fputs(next_custos(next_note_pitch, next_note_alteration,
                //             status), f);
                //     gregoriotex_print_change_line_clef(f, element);
              } else {

                if (prevElement && prevElement.type !== GregorioType.BAR) {
                  segmentElement.addVisualizer(Space.createFromWidth(this._context, this._context.clefchangespace));
                }

                segmentElement.addVisualizer(new Clef(this._context, element));

                if (prevElement && prevElement.type !== GregorioType.BAR) {
                  segmentElement.addVisualizer(Space.createFromWidth(this._context, this._context.clefchangespace));
                }

                //     /* the third argument is 0 or 1 according to the need
                //      * for a space before the clef */
                //     fprintf(f, "\\GreChangeClef{%c}{%d}{%c}{%d}{%c}{%d}{%d}%%\n",
                //             gregorio_clef_to_char(element->u.misc.clef.clef),
                //             element->u.misc.clef.line,
                //             (!element->previous || element->previous->type
                //              == GRE_BAR)? '0' : '1',
                //             clef_flat_height(element->u.misc.clef.clef,
                //                     element->u.misc.clef.line,
                //                     element->u.misc.clef.flatted),
                //             gregorio_clef_to_char(
                //                     element->u.misc.clef.secondary_clef),
                //             element->u.misc.clef.secondary_line,
                //             clef_flat_height(
                //                     element->u.misc.clef.secondary_clef,
                //                     element->u.misc.clef.secondary_line,
                //                     element->u.misc.clef.secondary_flatted));
              }
              break;

            case GregorioType.CUSTOS:
              // eslint-disable-next-line no-case-declarations
              const custos = new Custos(this._context);
              custos.setHeightFromPitch(element.misc.pitch);
              segmentElement.addVisualizer(custos);
              break;

            case GregorioType.MANUAL_CUSTOS:
              // eslint-disable-next-line no-case-declarations
              const custosManual = new Custos(this._context);
              custosManual.setHeightFromPitch(element.misc.pitch);
              // segmentElement.addInterelementSpace();
              segmentElement.addVisualizer(custosManual);
              break;

            case GregorioType.SPACE:
              switch (element.misc.info.space) {
                case GregorioSpace.LARGER_SPACE:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 1, false));
                  // fprintf(f, "\\GreEndOfElement{1}{0}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.GLYPH_SPACE:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 2, false));
                  // fprintf(f, "\\GreEndOfElement{2}{0}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.NEUMATIC_CUT:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 0, false));
                  // fprintf(f, "\\GreEndOfElement{0}{0}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.AD_HOC_SPACE:
                  // eslint-disable-next-line max-len
                  segmentElement.addVisualizer(
                    Space.createFromElementEnd(this._context, 0, false));

                  // fprintf(f, "\\GreAdHocSpaceEndOfElement{%s}{0}{%u}%%\n",
                  //   element -> u.misc.unpitched.info.ad_hoc_space_factor,
                  //   note_unit_count);
                  break;
                case GregorioSpace.GLYPH_SPACE_NB:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 2, true));
                  // fprintf(f, "\\GreEndOfElement{2}{1}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.LARGER_SPACE_NB:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 1, true));
                  // fprintf(f, "\\GreEndOfElement{1}{1}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.NEUMATIC_CUT_NB:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 0, true));
                  // fprintf(f, "\\GreEndOfElement{0}{1}{%u}%%\n",
                  //   note_unit_count);
                  break;
                case GregorioSpace.AD_HOC_SPACE_NB:
                  segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 0, true));
                  // fprintf(f, "\\GreAdHocSpaceEndOfElement{%s}{1}{%u}%%\n",
                  //   element -> u.misc.unpitched.info.ad_hoc_space_factor,
                  //   note_unit_count);
                  break;
                default:
                  console.error('Unknown space type: ', element.misc.info.space);
                  continue;
              }

              break;

            case GregorioType.BAR:
              // eslint-disable-next-line no-case-declarations
              if (prevElement) {
                segmentElement.addVisualizer(Space.createFromWidth(this._context, this._context.interwordspacenotes));
              }

              segmentElement.addVisualizer(determine_bar(this._context, element.misc.info.bar));
              if (element.misc.special_sign & GregorioSign.BAR_H_EPISEMA) {
                segmentElement.addVisualizer(new BarBrace(this._context));
              }

              if (element.misc.special_sign & GregorioSign.V_EPISEMA) {
                segmentElement.addVisualizer(new Ictus(this._context));
              }

              if (nextElement) {
                segmentElement.addVisualizer(Space.createFromWidth(this._context, this._context.interwordspacenotes));
              }

              break;

            default:
              // GRE_ELEMENT
              // segmentElement.addInterelementSpace();
              for (let glIndex = 0; glIndex < element.glyphs.length; glIndex++) {
                const glyph = element.glyphs[glIndex];
                const prevGlyph = element.glyphs[glIndex - 1];
                const nextGlyph = element.glyphs[glIndex + 1];

                switch (glyph.type) {
                  case GregorioType.SPACE:
                    switch (glyph.misc.info.space) {
                      case GregorioSpace.ZERO_WIDTH:
                        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 0));
                        break;
                      case GregorioSpace.HALF_SPACE:
                        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 22));
                        break;
                      case GregorioSpace.INTERGLYPH_SPACE:
                        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 1));
                        break;
                      default:
                        console.error('Unknown space type: ', element.misc.info.space);
                        continue;
                    }

                    break;
                  default:
                    if (glyph.type !== GregorioType.GLYPH) {
                      continue;
                    }

                    glyph.notes.fuse_to_next_glyph = compute_fused_shift(element.glyphs, glIndex + 1);

                    if (PunctaInclinata.includes(glyph.notes.glyph_type)) {
                      this.handleComposedMultinoteGlyph(element, glyph, glIndex, segmentElement);
                    } else if (this._specialNotes.includes(glyph.notes.glyph_type)) {
                      this.handleSpecialNote(glyph, element, segmentElement);
                    } else if (glyph.isSingleNote) {
                      if (glyph.notes.fuse_to_next_glyph > 0 && element.glyphs.length > 3) {
                        this.handleExceptionalFusing(glyph, element, glIndex, segmentElement);
                      } else {
                        this.handleSingleNoteGlyph(glyph, element, glIndex, segmentElement, prevGlyph);
                      }
                    } else {
                      // complex note
                      this.handleComplexNote(element, glyph, glIndex, segmentElement);
                    }
                    // eslint-disable-next-line no-case-declarations
                    this.handleMora(element, nextElement, glIndex, segmentElement);
                    // this.handleEpisemas(glyph, segmentElement);
                    if (glyph.hasVEpisema) {
                      segmentElement.addVisualizer(new Ictus(this._context, glyph));
                    }

                    if (nextGlyph && nextGlyph.type === GregorioType.GLYPH && nextGlyph.notes.glyph_type !== GregorioGlyphType.ALTERATION) {
                      if (is_fused(nextGlyph.notes.liquescentia)) {
                        const spaceCase = get_punctum_inclinatum_to_nobar_space_case(glyph, nextGlyph);
                        if (spaceCase >= 0) {
                          /* fuse from punctum inclinatum to nobar glyph */
                          segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, spaceCase));
                        } else {
                          segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 1));
                        }
                      } else if (is_puncta_inclinata(nextGlyph.notes.glyph_type) || nextGlyph.notes.glyph_type === GregorioGlyphType.PUNCTA_INCLINATA) {
                        if ((is_puncta_inclinata(glyph.notes.glyph_type) || glyph.notes.glyph_type === GregorioGlyphType.PUNCTA_INCLINATA) && nextGlyph.firstNote!.pitch == glyph.lastNote!.pitch) {
                          /* special case for unison puncta inclinata */
                          segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 23));
                        } else {
                          segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 9));
                        }
                      } else {
                        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, glyph.notes.glyph_type !== GregorioGlyphType.ALTERATION ? 0 : 2));
                      }
                    }
                }
              }

              // eslint-disable-next-line no-case-declarations
              const nextNextElement = segment.elements[elIndex + 2];

              if (nextElement && (nextElement.type === GregorioType.ELEMENT
                || (nextNextElement && nextElement.type == GregorioType.ALT &&
                  nextNextElement.type == GregorioType.ELEMENT))) {
                segmentElement.addVisualizer(Space.createFromElementEnd(this._context, 0, false));
              }

              break;
          }

          if (segmentElement.hasEndOfLine) {
            // finish here
            segmentElements.push(segmentElement);
            syllableElement.addSegments(segmentElements);

            if (segment.nabcNotations.length > 0) {
              segment.nabcNotations.forEach((nabc) => {
                const visualizer = new NabcSnippetVisualizer(this._context, nabc);
                segmentElement?.addNabcVisualizer(visualizer);
              });
            }

            if (segment.hasEpisema) {
              segmentElement.addVisualizer(new HorizontalEpisema(this._context, segment));
            }

            this.notations.push(syllableElement);

            // create new notation
            segmentElements = [];
            syllableElement = new ChantSyllableElement(this._context, syllable, sylIndex);
            segmentElement = undefined;
          }
        }

        if (segmentElement) {
          if (segment.nabcNotations.length > 0) {
            segment.nabcNotations.forEach((nabc) => {
              const visualizer = new NabcSnippetVisualizer(this._context, nabc);
              segmentElement!.addNabcVisualizer(visualizer);
            });
          }

          if (segment.hasEpisema) {
            segmentElement.addVisualizer(new HorizontalEpisema(this._context, segment));
          }

          segmentElements.push(segmentElement);
        }
      }

      syllableElement.addSegments(segmentElements);

      if (syllableElement.hasAnySegments) {
        this.notations.push(syllableElement);
      }

      sylIndex++;
    }
  }

  private getGlyph(glyphName: string, glyph: GregorioGlyph) {
    const visualizer = new GlyphVisualizer(this._context,
      new Glyph(glyphName, this._context.gabcFont, this._context.gabcGlyphSize),
      this._context.gabcColor,
      glyph.notes.notes, glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);
    glyph.addVisualizers(1);

    visualizer.setStaffPositionFromGlyph(glyph);
    return visualizer;
  }

  private handleSpecialNote(glyph: GregorioGlyph, element: GregorioElement, segmentElement: ChantSegmentElement) {
    switch (glyph.notes.glyph_type) {
      case GregorioGlyphType.TRIVIRGA:
        segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Virga], glyph));
        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 4));
      /* fall through */
      case GregorioGlyphType.BIVIRGA:
        segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Virga], glyph));
        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 4));
        segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Virga], glyph));
        break;
      case GregorioGlyphType.TRISTROPHA:
        segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Stropha], glyph));
        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 5));
      /* fall through */
      case GregorioGlyphType.DISTROPHA:
        segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Stropha], glyph));
        segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, 5));
        if (glyph.notes.notes[0].liquescentia === GregorioLiquescentia.AUCTUS_ASCENDENS) {
          segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.StrophaAucta], glyph));
        } else {
          segmentElement.addVisualizer(this.getGlyph(SHAPE[SHAPE.Stropha], glyph));
        }
        break;
    }
  }

  private handleMora(element: GregorioElement, next_element: GregorioElement, glyph_i: number, segmentElement: ChantSegmentElement) {
    const glyph = element.glyphs[glyph_i];
    const next_glyph = element.glyphs[glyph_i + 1];
    const next_next_glyph = element.glyphs[glyph_i + 2];

    for (let i = 0; i < glyph.notes.notes.length; i++) {
      const note = glyph.notes.notes[i];

      if (note.signs & GregorioSign.PUNCTUM_MORA || note.signs & GregorioSign.AUCTUM_DUPLEX) {
        const next_note = glyph.notes.notes[i + 1];
        const next_next_note = glyph.notes.notes[i + 2];
        const prev_note = glyph.notes.notes[i - 1];

        if (note.signs & GregorioSign.PUNCTUM_MORA) {
          const mora = Mora.writePunctumMora(
            this._context, glyph, note, i,
            glyph.notes.fuse_to_next_glyph,
            next_glyph, next_next_glyph,
            next_note, next_next_note,
            prev_note, next_element);
          segmentElement.addVisualizer(mora);
        }

        if (note.signs & GregorioSign.AUCTUM_DUPLEX) {
          const visualizers = Mora.writeAuctumDuplex(
            this._context,
            note, prev_note,
          );
          segmentElement.addVisualizers(visualizers);
        }
      }
    }
  }

  private handleComplexNote(
    element: GregorioElement,
    glyph: GregorioGlyph,
    glyphIndex: number,
    segmentElement: ChantSegmentElement) {

    if (glyph.notes.glyph_type === GregorioGlyphType.TORCULUS_RESUPINUS_FLEXUS) {
      const leading_shape = determine_leading_shape(glyph);

      const visualizer = new GlyphVisualizer(this._context,
        new Glyph(leading_shape,
          this._context.gabcFont,
          this._context.gabcGlyphSize),
        this._context.gabcColor,
        glyph.notes.notes.slice(0, 1), glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);

      glyph.notes.glyph_type = GregorioGlyphType.PORRECTUS_FLEXUS_NO_BAR;
      glyph.notes.startFrom = 1;
      const [shape] = gregoriotex_determine_glyph_name(element.glyphs, glyphIndex);
      const visualizer2 = new GlyphVisualizer(this._context,
        new Glyph(shape, this._context.gabcFont, this._context.gabcGlyphSize),
        this._context.gabcColor,
        glyph.notes.notes,
        glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);

        glyph.addVisualizers(2);

      visualizer.setStaffPositionFromGlyph(glyph);
      segmentElement.addVisualizer(visualizer);
      visualizer2.setStaffPositionFromGlyph(glyph);
      segmentElement.addVisualizer(visualizer2);

    } else if (glyph.notes.glyph_type === GregorioGlyphType.SCANDICUS && element.glyphs.length > 3) {
      this.handleComposedMultinoteGlyph(element, glyph, glyphIndex, segmentElement);
    } else {
      const [shape] = gregoriotex_determine_glyph_name(element.glyphs, glyphIndex);

      const visualizer = new GlyphVisualizer(this._context,
        new Glyph(shape, this._context.gabcFont, this._context.gabcGlyphSize),
        this._context.gabcColor, glyph.notes.notes, glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);
      glyph.addVisualizers(1);

      visualizer.setStaffPositionFromGlyph(glyph);
      segmentElement.addVisualizer(visualizer);
    }
  }

  private handleComposedMultinoteGlyph(
    element: GregorioElement,
    glyph: GregorioGlyph,
    glyphIndex: number,
    segmentElement: ChantSegmentElement,
  ) {
    for (let i = 0; i < glyph.notes.notes.length; i++) {
      const note = glyph.notes.notes[i];
      const prevNote = glyph.notes.notes[i - 1];
      this.handleNoteGlyph(note, element, glyph, glyphIndex, segmentElement, prevNote);
    }
  }

  private handleSingleNoteGlyph(
    glyph: GregorioGlyph,
    element: GregorioElement,
    glyphIndex: number,
    segmentElement: ChantSegmentElement,
    prevGlyph?: GregorioGlyph,
  ) {
    this.handleNoteGlyph(
      glyph.firstNote!, element, glyph, glyphIndex, segmentElement, prevGlyph ? prevGlyph.lastNote : undefined);
  }

  private handleNoteGlyph(
    note: GregorioNoteElement,
    element: GregorioElement,
    glyph: GregorioGlyph,
    glyphIndex: number,
    segmentElement: ChantSegmentElement,
    previous?: GregorioNoteElement,
  ) {
    if (note.shape === GregorioShape.PUNCTUM) {
      switch (note.liquescentia) {
        case GregorioLiquescentia.AUCTUS_ASCENDENS:
          note.shape = GregorioShape.PUNCTUM_AUCTUS_ASCENDENS;
          break;
        case GregorioLiquescentia.AUCTUS_DESCENDENS:
          note.shape = (GregorioShape.PUNCTUM_AUCTUS_DESCENDENS);
          break;
        case GregorioLiquescentia.INITIO_DEBILIS:
          if (element.glyphs[0].notes.fuse_to_next_glyph > 0) {
            break;
          }
        /* fall through */
        case GregorioLiquescentia.DEMINUTUS:
          /* this is a currenly unused, but we keep it as a fallback case */
          /* LCOV_EXCL_START */
          note.shape = (GregorioShape.PUNCTUM_DEMINUTUS);
          break;
        /* LCOV_EXCL_STOP */
        default:
          break;
      }
    }

    const spaceCase = get_punctum_inclinatum_space_case(note, previous);
    if (spaceCase >= 0) {
      segmentElement.addVisualizer(Space.createFromGlyphEnd(this._context, spaceCase));
    }

    const glyphName = determine_note_glyph_name(note, element.glyphs, glyphIndex);
    const visualizer = new GlyphVisualizer(
      this._context,
      new Glyph(glyphName, this._context.gabcFont, this._context.gabcGlyphSize),
      this._context.gabcColor, glyph.notes.notes, glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);
    glyph.addVisualizers(1);

    visualizer.setStaffPositionFromNote(note);

    segmentElement.addVisualizer(visualizer);
  }

  private handleExceptionalFusing(
    glyph: GregorioGlyph,
    element: GregorioElement,
    glyphIndex: number,
    segmentElement: ChantSegmentElement,
  ) {
    const prevGlyph = element.glyphs[glyphIndex - 1];
    prevGlyph.notes.notes.push(glyph.firstNote!);
    prevGlyph.notes.glyph_type = GregorioGlyphType.PORRECTUS;
    prevGlyph.notes.liquescentia = GregorioLiquescentia.AUCTUS_DESCENDENS_INITIO_DEBILIS;
    element.glyphs = element.glyphs.splice(glyphIndex, 1);
    element.glyphs[glyphIndex - 1] = prevGlyph;

    const [shape] = gregoriotex_determine_glyph_name(element.glyphs, glyphIndex - 1);
    const visualizer = new GlyphVisualizer(
      this._context,
      new Glyph(shape,
        this._context.gabcFont,
        this._context.gabcGlyphSize),
      this._context.gabcColor,
      glyph.notes.notes,
      glyph.notes.is_cavum, glyph.notes.fuse_to_next_glyph);
    glyph.addVisualizers(1);
      
    visualizer.setStaffPositionFromGlyph(glyph);

    segmentElement.popVisualizer();
    segmentElement.addVisualizer(visualizer);

    // this.handleComplexNote(element, prevGlyph, glyphIndex - 1, segmentElement);
  }

  protected prepareLines(score: GregorioScore) {
    this.lines = [];
    const _distanceConverter = new DistanceConverter(this._context);

    // get dropcap
    let dropcap: DropCap | undefined = undefined;
    const initial = score.metadata.getField<number>('initial');
    if (initial !== 0) {
      const firstNotationWithLyrics = this.notations.find((n) => n.hasLyric);
      if (firstNotationWithLyrics) {
        dropcap = firstNotationWithLyrics.lyric!.generateDropCap();
      }
    }

    let syllableIndex = 0;
    while (syllableIndex < this.notations.length) {
      const line = new ChantLine(this._context, dropcap, dropcap ? this._annotation : undefined);

      const notationsAddedToLine = line.performLayout(this.notations, syllableIndex);

      if (this.lines.length > 0) {
        const prevLine = this.lines[this.lines.length - 1];
        line.bounds.setY(line.bounds.y + prevLine.bottom + _distanceConverter.lengthToPx(this._context.spaceabovelines));
      }

      syllableIndex += notationsAddedToLine;

      if (notationsAddedToLine === 0) {
        console.log('No notations added. Breaking.');
        break;
      }

      if (dropcap) {
        dropcap = undefined;
      }

      this.lines.push(line);
    }
  }
}
